import chroma, { scale } from 'chroma-js';
import { MethodMemoryCache } from './utils/MethodMemoryCache';
import { wcagContrastRatio } from './a11yUtils';
import { getColor } from './utils';
import { getAccessibleColorFromBackground, getColorBackgroundForCalculations } from './tokenOverrides';
// Based on the Table at https://sail.stripe.me/foundations/color/#color-themes
// Half steps have been added between 200-900 to support differences between full step and half step contrasts
export const SailContrastMap = {
    0: {
        targetContrastRatio: 0,
        variance: [
            0,
            0
        ]
    },
    25: {
        targetContrastRatio: 1.03,
        variance: [
            -0.01,
            0.0
        ]
    },
    50: {
        targetContrastRatio: 1.06,
        variance: [
            -0.01,
            0.0
        ]
    },
    100: {
        targetContrastRatio: 1.16,
        variance: [
            -0.01,
            0.01
        ]
    },
    150: {
        targetContrastRatio: 1.4,
        variance: [
            -0.04,
            0.04
        ]
    },
    200: {
        targetContrastRatio: 1.68,
        variance: [
            -0.06,
            0.06
        ]
    },
    250: {
        targetContrastRatio: 1.98,
        variance: [
            -0.06,
            0.06
        ]
    },
    300: {
        targetContrastRatio: 2.28,
        variance: [
            -0.08,
            0.08
        ]
    },
    350: {
        targetContrastRatio: 2.74,
        variance: [
            -0.08,
            0.08
        ]
    },
    400: {
        targetContrastRatio: 3.2,
        variance: [
            0.0,
            0.16
        ]
    },
    450: {
        targetContrastRatio: 4.0,
        variance: [
            0.0,
            0.16
        ]
    },
    500: {
        targetContrastRatio: 4.8,
        variance: [
            -0.01,
            0.16
        ]
    },
    550: {
        targetContrastRatio: 5.87,
        variance: [
            -0.01,
            0.16
        ]
    },
    600: {
        targetContrastRatio: 6.94,
        variance: [
            -0.16,
            0.16
        ]
    },
    650: {
        targetContrastRatio: 8.27,
        variance: [
            -0.16,
            0.16
        ]
    },
    700: {
        targetContrastRatio: 9.6,
        variance: [
            -0.16,
            0.16
        ]
    },
    750: {
        targetContrastRatio: 11.2,
        variance: [
            -0.16,
            0.16
        ]
    },
    800: {
        targetContrastRatio: 12.8,
        variance: [
            -0.24,
            0.24
        ]
    },
    850: {
        targetContrastRatio: 14.9,
        variance: [
            -0.24,
            0.24
        ]
    },
    875: {
        targetContrastRatio: 15.95,
        variance: [
            -0.24,
            0.24
        ]
    },
    900: {
        targetContrastRatio: 17.0,
        variance: [
            -0.24,
            0.24
        ]
    }
};
const colorScaleDomain = [
    0,
    1
];
// We utilize Chroma.js here instead of d3-color because it can use the oklch colorspace
// Oklch has significantly better hue uniformity than the colorspaces available in d3-color (lab, hsl)
export const getColorScale = (colors)=>{
    return scale([
        'white',
        ...colors,
        'black'
    ]).domain([
        colorScaleDomain[0],
        colorScaleDomain[1]
    ]).mode('oklch').out('hex');
};
export const getContrastFunctionForColorScale = (// eslint-disable-next-line @typescript-eslint/no-explicit-any
scaleFunc, baseColorIntercept = 0.5)=>{
    return (i)=>wcagContrastRatio(scaleFunc(i), scaleFunc(baseColorIntercept));
};
const findColorIntercept = (chromaScale, color)=>{
    let intercept;
    const colorL = color.oklch()[0];
    // If the input color is black, return an intercept of 1
    if (colorL <= 0.1) {
        return 1;
    }
    // Temporarily configure the Color scale to output oklch values instead of Hex
    chromaScale.out('oklch');
    let bestColorAndTolerance;
    // We do a search here throughout different Find an color that has a lower difference than our tolerance (0.005)
    for(let i = 0; i < 100; i += 1){
        const interceptColor = chromaScale(i / 100);
        const difference = Math.abs(interceptColor[0] - colorL);
        if (!bestColorAndTolerance || difference < bestColorAndTolerance.difference) {
            bestColorAndTolerance = {
                intercept: i / 100,
                difference
            };
        }
        if (difference <= 0.005) {
            intercept = i / 100;
            break;
        }
    }
    if (!bestColorAndTolerance) {
        throw new Error('Failed to find color while iterating through intercepts');
    }
    // If no matching color is found, repeat the search with a higher tolerance
    if (!intercept) {
        intercept = bestColorAndTolerance.intercept;
    }
    // Reconfigure Hex color output
    chromaScale.out('hex');
    return intercept;
};
// Prevent the returned color from being #FFFFFF or #000000
export const contrastHeadroom = SailContrastMap[50].targetContrastRatio;
export const binaryContrastSearch = ({ sourceLevel, targetLevel, contrastFunction, enforceContrastHeadroom = true, adjustedCenter })=>{
    const positiveTolerance = SailContrastMap[targetLevel].variance[1];
    const targetContrast = Math.abs(SailContrastMap[sourceLevel].targetContrastRatio - SailContrastMap[targetLevel].targetContrastRatio);
    // Stop search after 15 steps if a value within our target delta cannot be found
    let maxSteps = 20;
    let start = colorScaleDomain[0];
    let end = colorScaleDomain[1];
    let mid = (start + end) / 2;
    let directionOfIncreasingContrast;
    let maxContrast;
    // Scale the current color towards White
    if (sourceLevel > targetLevel) {
        end = adjustedCenter !== null && adjustedCenter !== void 0 ? adjustedCenter : mid;
        mid = end;
        directionOfIncreasingContrast = -1;
        maxContrast = contrastFunction(colorScaleDomain[0]);
    } else {
        start = adjustedCenter !== null && adjustedCenter !== void 0 ? adjustedCenter : mid;
        mid = start;
        directionOfIncreasingContrast = 1;
        maxContrast = contrastFunction(colorScaleDomain[1]);
    }
    let step = Math.abs(start - end);
    const contrastAgainstBackground = enforceContrastHeadroom ? contrastHeadroom : 0;
    let targetWithHeadroom = Math.min(Math.abs(maxContrast - contrastAgainstBackground), targetContrast);
    // If targetWithHeadroom is below 1, add it 1 so contrast can be calculated correctly
    targetWithHeadroom = Math.max(targetWithHeadroom, 1 + targetWithHeadroom % 1.0);
    const positiveTarget = targetWithHeadroom + positiveTolerance;
    let solution = contrastFunction(mid);
    // Search for a point within our Color scale that satisfies the target
    // contrast ratio within a specific tolerance
    while((solution > positiveTarget || solution < targetWithHeadroom) && maxSteps){
        maxSteps -= 1;
        step /= 2;
        // Reverse the last step if we are past an end stop, this can happen if the start/end are extremely close
        if (mid < start || mid > end) {
            mid += directionOfIncreasingContrast * step * 2;
            break;
        }
        if (solution > targetWithHeadroom) {
            mid -= directionOfIncreasingContrast * step;
        } else {
            mid += directionOfIncreasingContrast * step;
        }
        solution = contrastFunction(mid);
    }
    return mid;
};
const getSourceSailContrastLevel = (contrastAgainstLevelZero)=>{
    const contrastEntries = Object.entries(SailContrastMap);
    for(let i = 1; i < contrastEntries.length; i += 1){
        const [level, contrastDetails] = contrastEntries[i];
        const [prevLevel, prevLevelDetails] = contrastEntries[i - 1];
        const midpoint = (contrastDetails.targetContrastRatio + prevLevelDetails.targetContrastRatio) / 2;
        if (prevLevelDetails.targetContrastRatio < contrastAgainstLevelZero && contrastAgainstLevelZero < contrastDetails.targetContrastRatio) {
            if (contrastAgainstLevelZero > midpoint) {
                return parseInt(level, 10);
            } else {
                return parseInt(prevLevel, 10);
            }
        }
    }
    // contrastAgainstLevelZero is above our Contrast scale, return the highest level possible
    return 900;
};
export const getClosestContrastLevel = (contrastLevel)=>{
    // Check to see if the contrast level is already in the map
    if (SailContrastMap[contrastLevel]) {
        return contrastLevel;
    }
    const validConstrastLevels = Object.keys(SailContrastMap).map(Number);
    // Otherwise binary search for closest value, preferring larger value if there's a tie
    let left = 0;
    let right = validConstrastLevels.length - 1;
    while(left < right){
        const mid = Math.floor((left + right) / 2);
        if (validConstrastLevels[mid] < contrastLevel) {
            left = mid + 1;
        } else {
            right = mid;
        }
    }
    if (left === 0) return validConstrastLevels[0];
    const prevDiff = contrastLevel - validConstrastLevels[left - 1];
    const currDiff = validConstrastLevels[left] - contrastLevel;
    return prevDiff < currDiff ? validConstrastLevels[left - 1] : validConstrastLevels[left];
};
const getTargetSailContrastLevel = (sourceLevel, targetContrast)=>{
    if (sourceLevel - targetContrast >= 50 || sourceLevel > 900 - sourceLevel) {
        const targetLevel = Math.max(50, sourceLevel - targetContrast);
        return getClosestContrastLevel(targetLevel);
    } else {
        const targetLevel = Math.min(900, sourceLevel + targetContrast);
        return getClosestContrastLevel(targetLevel);
    }
};
const getSailContrastRangeForText = (contrastAgainstWhite, contrastAgainstBlack)=>{
    if (contrastAgainstWhite >= SailContrastMap[400].targetContrastRatio || contrastAgainstBlack < SailContrastMap[400].targetContrastRatio) {
        // Force return #FFFFFF
        return [
            900,
            0
        ];
    } else {
        // Make a darker color
        return [
            0,
            700
        ];
    }
};
const getAccessibleColorCache = new MethodMemoryCache();
export const getAccessibleColor = (rawColorInput, targetContrastDelta)=>{
    // This is an expensive (in terms of CPU) and widely used method (from many different entry points), so we have a cache around it
    const targetContrastDeltaCacheKey = Array.isArray(targetContrastDelta) ? targetContrastDelta.join('-') : targetContrastDelta;
    const cacheKey = `Color:${rawColorInput.toLowerCase()}-Contrast:${targetContrastDeltaCacheKey}`;
    const cachedResult = getAccessibleColorCache.get(cacheKey);
    if (cachedResult) {
        return cachedResult;
    }
    const colorHex = getColor(rawColorInput).formatHex();
    const colorChroma = chroma(colorHex);
    // Generate a Pastel color from the input color.
    // Pastel definition: Pastels or pastel colors belong to a pale family of colors, which, when described in the HSV color space, have high value and low saturation (https://en.wikipedia.org/wiki/Pastel_(color))
    const colorPastel = colorChroma.set('oklch.l', 1.0).set('oklch.c', '*0.80').set('oklch.h', '*0.95');
    let sourceSailLevel;
    let targetSailLevel;
    const colorScale = getColorScale([
        colorPastel,
        colorChroma
    ]).correctLightness();
    const adjustedCenter = findColorIntercept(colorScale, colorChroma);
    const colorScaleContrast = getContrastFunctionForColorScale(colorScale, adjustedCenter);
    // Calculate the appropriate Source and Target level for the current color
    if (!Array.isArray(targetContrastDelta)) {
        // Sail contrasts are all based on contrast against White
        const contrastAgainstLevelZero = colorScaleContrast(0);
        sourceSailLevel = getSourceSailContrastLevel(contrastAgainstLevelZero);
        targetSailLevel = getTargetSailContrastLevel(sourceSailLevel, targetContrastDelta);
    } else {
        // Use specific contrast levels regardless of the current color's level
        [sourceSailLevel, targetSailLevel] = targetContrastDelta;
    }
    const contrastIntercept = binaryContrastSearch({
        targetLevel: targetSailLevel,
        sourceLevel: sourceSailLevel,
        contrastFunction: colorScaleContrast,
        adjustedCenter
    });
    const contrastInterceptResult = colorScale(contrastIntercept);
    getAccessibleColorCache.set(cacheKey, contrastInterceptResult);
    return contrastInterceptResult;
};
export const getAccessibleColorFromBackgroundValue = (backgroundColor, darkContrast, lightContrast)=>{
    const colorHex = getColor(backgroundColor).formatHex();
    const colorChroma = chroma(colorHex);
    if (colorChroma.luminance() < 0.3) {
        return getAccessibleColor(backgroundColor, darkContrast);
    }
    return getAccessibleColor(backgroundColor, lightContrast);
};
export const deriveColorFromValue = (colorValue, darkContrast, lightContrast)=>{
    const colorHex = getColor(colorValue).formatHex();
    const colorChroma = chroma(colorHex);
    if (colorChroma.luminance() < 0.1) {
        return getAccessibleColor(colorValue, darkContrast);
    }
    return getAccessibleColor(colorValue, lightContrast);
};
export const getHexColorLuminance = (colorHex)=>{
    const colorChroma = chroma(colorHex);
    return colorChroma.luminance();
};
export const hasLightLuminance = (colorHex)=>{
    return getHexColorLuminance(colorHex) > 0.9;
};
// TODO: Take Font size and weight as inputs to determine the desired contrast level
export const getAccessibleTextColor = (colorStr)=>{
    const colorScale = getColorScale([
        getColor(colorStr).formatHex()
    ]);
    const colorScaleContrast = getContrastFunctionForColorScale(colorScale);
    const contrastAgainstWhite = colorScaleContrast(0);
    const contrastAgainstBlack = colorScaleContrast(1);
    const [sourceLevel, targetLevel] = getSailContrastRangeForText(contrastAgainstWhite, contrastAgainstBlack);
    if (sourceLevel === 900) {
        return '#ffffff';
    }
    const contrastIntercept = binaryContrastSearch({
        targetLevel,
        sourceLevel,
        contrastFunction: colorScaleContrast,
        enforceContrastHeadroom: false
    });
    return colorScale(contrastIntercept);
};
// Compares the border color against the background color to see if the contrast ratio is high enough.
// for example, with a white primary color (becoming the "smart default" for button border) and a white background,
// the contrast ratio is too low, so we need to adjust the border color to be darker.
export const getAccessibleBorderColor = (colorStr, theme, isHighlight = false)=>{
    const colorHexValue = getColor(colorStr).formatHex();
    const backgroundColorHexValue = getColor(getColorBackgroundForCalculations(theme)).formatHex();
    const contrastRatio = chroma.contrast(colorHexValue, backgroundColorHexValue);
    // 1.35:1 is our default border to background color contrast ratio.
    if (contrastRatio < 1.35) {
        // border color in sail-next is neutral100, #d8dee4: https://sail.stripe.me/tokens/color/#borders
        const highContrastRangeValue = isHighlight ? 600 : 800;
        const lowContrastRangeValue = isHighlight ? 300 : 100;
        const borderColor = getAccessibleColorFromBackground(theme, [
            900,
            highContrastRangeValue
        ], [
            0,
            lowContrastRangeValue
        ]);
        return borderColor;
    }
    return colorStr;
};

//# sourceMappingURL=contrastUtils.js.map